import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useFilters, Panel, Loading, Empty, PageHeader, ExportBar, downloadPdf, RiskBadge, ScoreBadge } from "./_shared";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MessageSquareQuote, ClipboardList, Users2, GraduationCap } from "lucide-react";
import { toast } from "sonner";

export default function FacilitatorDebrief() {
  const { options, filters } = useFilters();
  const batches = options?.batches || [];
  const [batchId, setBatchId] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (batches.length && !batchId) setBatchId(filters.batch_id !== "all" ? filters.batch_id : batches[0].id);
  }, [batches]);

  useEffect(() => {
    if (!batchId) return;
    setLoading(true);
    api.get(`/analytics/facilitator-debrief/${batchId}`).then((r) => setD(r.data)).finally(() => setLoading(false));
  }, [batchId]);

  return (
    <div data-testid="facilitator-debrief-page">
      <PageHeader title="Facilitator Debrief Report"
        description="An auto-generated briefing you can read in under five minutes before the cohort debrief session."
        right={d && <ExportBar onPdf={() => downloadPdf(`/analytics/facilitator-debrief/${batchId}/report.pdf`, `debrief-${d.batch_name}.pdf`).catch(() => toast.error("Download failed"))} pdfLabel="Download briefing" />} />

      <div className="mb-6 max-w-xs">
        <label className="text-[11px] text-stone-500 mb-1 block">Select batch</label>
        <Select value={batchId} onValueChange={setBatchId}>
          <SelectTrigger data-testid="debrief-batch-select" className="h-10 border-stone-300 rounded-lg bg-white">
            <SelectValue placeholder="Choose a batch" />
          </SelectTrigger>
          <SelectContent>
            {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <Loading /> : !d ? <Empty label="Select a batch to generate the debrief." /> : (
        <div className="space-y-6">
          <Panel testid="debrief-summary">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h2 className="font-display text-xl font-semibold text-stone-900">{d.batch_name}</h2>
                <p className="text-sm text-stone-500">{d.program} • {d.facilitator}</p>
              </div>
              <div className="flex gap-6 text-center">
                <div><div className="font-display text-2xl font-semibold text-stone-900">{d.enrolled}</div><div className="text-[10px] uppercase text-stone-500">Enrolled</div></div>
                <div><div className="font-display text-2xl font-semibold text-stone-900">{d.completed}</div><div className="text-[10px] uppercase text-stone-500">Completed</div></div>
                <div><div className="font-display text-2xl font-semibold text-[#b81f24]">{d.avg_score}</div><div className="text-[10px] uppercase text-stone-500">Avg score</div></div>
              </div>
            </div>
            <p className="mt-4 text-sm text-stone-700 bg-stone-50 rounded-xl p-4 leading-relaxed">{d.patterns}</p>
          </Panel>

          <div className="grid md:grid-cols-2 gap-6">
            <Panel title="Top 5 Strengths">
              <ol className="space-y-2">
                {d.top_strengths.map((s, i) => (
                  <li key={s.competency} className="flex items-center justify-between text-sm">
                    <span className="text-stone-700"><span className="text-stone-400 mr-2">{i + 1}.</span>{s.competency}</span>
                    <ScoreBadge value={s.score} />
                  </li>
                ))}
              </ol>
            </Panel>
            <Panel title="Top 5 Development Areas">
              <ol className="space-y-2">
                {d.top_development.map((s, i) => (
                  <li key={s.competency} className="flex items-center justify-between text-sm">
                    <span className="text-stone-700"><span className="text-stone-400 mr-2">{i + 1}.</span>{s.competency}</span>
                    <ScoreBadge value={s.score} />
                  </li>
                ))}
              </ol>
            </Panel>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Panel title="Common Mistakes" right={<ClipboardList className="w-4 h-4 text-[#b45309]" />}>
              <ul className="space-y-1.5 text-sm text-stone-700">
                {d.common_mistakes.length === 0 && <li className="text-stone-400">—</li>}
                {d.common_mistakes.map((m) => <li key={m} className="flex gap-2"><span className="text-[#b45309]">•</span>{m}</li>)}
              </ul>
            </Panel>
            <Panel title="Typical Learner Behaviours" right={<Users2 className="w-4 h-4 text-emerald-600" />}>
              <ul className="space-y-1.5 text-sm text-stone-700">
                {d.typical_behaviours.length === 0 && <li className="text-stone-400">—</li>}
                {d.typical_behaviours.map((m) => <li key={m} className="flex gap-2"><span className="text-emerald-600">•</span>{m}</li>)}
              </ul>
            </Panel>
          </div>

          <Panel title="Recommended Debrief Questions" right={<MessageSquareQuote className="w-4 h-4 text-[#b81f24]" />} testid="debrief-questions">
            <div className="space-y-2">
              {d.debrief_questions.map((q, i) => (
                <div key={i} className="flex gap-3 bg-stone-50 rounded-xl p-3">
                  <span className="w-6 h-6 rounded-full bg-[#b81f24] text-white text-xs flex items-center justify-center font-semibold shrink-0">{i + 1}</span>
                  <span className="text-sm text-stone-700">{q}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Suggested Facilitator Activities">
            <div className="grid sm:grid-cols-3 gap-3">
              {d.suggested_activities.map((a, i) => (
                <div key={i} className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 text-sm text-stone-700">{a}</div>
              ))}
            </div>
          </Panel>

          <div className="grid md:grid-cols-2 gap-6">
            <Panel title="Participants Requiring Additional Coaching">
              {d.needs_coaching.length === 0 ? <p className="text-sm text-stone-400">None flagged 🎉</p> : (
                <div className="space-y-2">
                  {d.needs_coaching.map((p) => (
                    <div key={p.name} className="flex items-center justify-between text-sm border-b border-stone-100 pb-2">
                      <div>
                        <span className="text-stone-800">{p.name}</span>
                        <div className="flex flex-wrap gap-1 mt-1">{p.flags.map((f) => <RiskBadge key={f} flag={f} />)}</div>
                      </div>
                      <ScoreBadge value={p.score} />
                    </div>
                  ))}
                </div>
              )}
            </Panel>
            <Panel title="Ready for Certification" right={<GraduationCap className="w-4 h-4 text-emerald-600" />}>
              {d.ready_for_certification.length === 0 ? <p className="text-sm text-stone-400">None ready yet.</p> : (
                <div className="space-y-2">
                  {d.ready_for_certification.map((p) => (
                    <div key={p.name} className="flex items-center justify-between text-sm border-b border-stone-100 pb-2">
                      <span className="text-stone-800">{p.name}</span>
                      <span className="text-sm font-semibold text-emerald-700 tabular-nums">{p.readiness}%</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
